<template>
  <div :class="{ hidden: !twoFactorAuthData.active }" class="bg-white py-12 px-7 rounded-lg shadow-ts1 w-[96%] md:w-full max-w-[580px] h-[548px]">
    <h1 class="text-blue text-3xl lg:text-[40px] leading-10 lg:leading-[72px] mb-9 text-center font-display font-bold" :class="{'lg:!leading-[40px]': Language === 'es'}">
      {{ $t('two_factor_authentication') }}
    </h1>
    <p class="text-gray-900 text-center text-base leading-7 mb-10 max-w-[366px] mx-auto" v-html="$t('two_factor_authentication_code_text')"></p>
    <div class="">
      <p class="text-gray-900 text-base mb-3 text-center">{{ $t('two_factor_code') }}</p>
      <div class="flex gap-2 justify-center w-fit m-auto">
        <div v-for="(digit, index) in verificationCode" :key="index" class="verification-box">
          <input
            class="w-[42px] h-[50px] text-[35px] font-bold text-center p-0 caret-transparent selection:bg-transparent"
            :class="[
              hasError === true ? 'border border-orange border-solid bg-[#EA50341A] focus:border-orange' : '',
              hasError === false ? 'border border-solid border-green bg-[#20B3811A] focus:border-green' : '',
            ]"
            ref="inputs"
            type="text"
            v-model="verificationCode[index]"
            maxlength="1"
            @input="handleInputChange(index)"
            @focus="handleFocus($event.target)"
            @keyup="handleKeyDown($event, index)"
            @paste="handlePaste"
          />
        </div>
      </div>
      <div>
        <span v-if="hasError === true" class="text-orange text-xxs text-center table mx-auto mt-1">{{ $t('two_factor_code_error') }}</span>
        <span v-else-if="hasError === false" class="text-green text-xxs text-center table mx-auto mt-1">{{ $t('two_factor_code_success') }}</span>
      </div>
    </div>
    <div class="flex justify-center mt-12">
      <button
        :class="[isCodeFull ? 'bg-blue' : 'bg-gray-300 pointer-events-none']"
        @click="next()"
        class="w-48 h-[50px] text-base font-bold rounded-md text-white flex justify-center items-center"
      >
        {{ $t('continue').tTitle() }}
      </button>
    </div>
    <div
      @click="back()"
      class="w-[70px] px-2.5 flex justify-center cursor-pointer rounded-xl mx-auto mt-7 bg-[#2B63E333] font-body text-xs leading-[21px] text-blue items-center gap-1.5"
    >
      <img class="h-3 w-3 inline" src="../../assets/img/back-arrow-blue.svg" />
      {{ $t('back').tTitle() }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Base64 } from 'js-base64'
import config from '../../config'
import { service } from '@/main'

export default {
  props: ['TwoFactorAuthStep2', 'data', 'is2FASetup'],
  data() {
    return {
      twoFactorAuthData: this.TwoFactorAuthStep2,
      verificationCode: ['', '', '', '', '', ''],
      isCodeFull: false,
      hasError: '',
      // isCorrect: false,
    }
  },
  watch: {
    // watcher for the i18n language change
    '$i18n.locale'(newVal, oldVal) {
      this.$validator.reset()
      if (newVal === 'fa' || oldVal === 'fa') this.verificationCode.reverse()
    },
  },
  computed: {
    ...mapGetters({
      User: 'user/all',
      Language: 'language/code',
      logoLoaderActive: 'logoLoader/active',
    }),
  },
  mounted() {
    this.$toasted.clear() //clear toasted notifications before the step change
    this.$bus.$emit('ChangeLang', this.Language)
    if (this.Language === 'fa') {
      this.$refs.inputs[5].focus()
    } else {
      this.$refs.inputs[0].focus()
    }
    const thisCopy = this
    window.addEventListener('popstate', function (event) {
      event.preventDefault()
      thisCopy.$emit('back', thisCopy.is2FASetup)
    })
  },
  methods: {
    copyTOTP() {
      const el = document.createElement('textarea')
      el.value = this.TwoFactorAuthStep1.totp
      el.setAttribute('readonly', '')
      el.style.position = 'absolute'
      el.style.left = '-9999px'
      document.body.appendChild(el)
      const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      if (selected) {
        document.getSelection().removeAllRanges()
        document.getSelection().addRange(selected)
      }
      this.$toasted.success(this.$t('copied'))
    },
    handleInputChange(index) {
      // Remove any non-numeric characters
      this.verificationCode[index] = this.verificationCode[index].replace(/\D/g, '')

      this.checkIFCodeIsFull()

      // If the input is a valid number, move to the next/previous field
      if (this.verificationCode[index] !== '') {
        const isRTL = this.Language === 'fa'
        if (isRTL) {
          if (index > 0) {
            this.$refs.inputs[index - 1].focus()
          }
        } else {
          if (index < this.verificationCode.length - 1) {
            this.$refs.inputs[index + 1].focus()
          }
        }
      }
    },
    handleKeyDown(e, index) {
      if (!/^[0-9]{1}$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && !e.metaKey) {
        e.preventDefault()
      }
      // RTL Detection: Check if verificationCode contains RTL characters (Arabic/Hebrew)
      const isRTL = this.Language === 'fa'
      // Handle Delete or Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        let indexToRemove = index
        // In LTR, backspace/delete moves to the left, in RTL, it moves to the right
        if (isRTL) {
          if (index < this.verificationCode.length - 1 && this.verificationCode[index] == '') {
            this.verificationCode.splice(indexToRemove, 1, '')
            indexToRemove = index + 1
          }
        } else {
          if (index > 0 && this.verificationCode[index] == '') {
            this.verificationCode.splice(indexToRemove, 1, '')
            indexToRemove = index - 1
          }
        }
        // Focus the correct input
        this.$refs.inputs[indexToRemove].focus()
        this.checkIFCodeIsFull()
      }
    },
    handleFocus(target) {
      target.select()
    },
    handlePaste(event) {
      event.preventDefault()
      const pasteData = event.clipboardData.getData('text/plain').trim()
      if (pasteData.length > 6) return
      const isRTL = this.Language === 'fa'
      const digits = pasteData.split('').slice(0, 6)
      const processedDigits = [...digits]
      isRTL ? processedDigits.reverse() : processedDigits
      processedDigits.forEach((digit, index) => {
        if (/^[0-9]$/.test(digit)) {
          this.$refs.inputs[index].value = digit
          this.verificationCode[index] = digit
        }
      })
      this.checkIFCodeIsFull()
      this.$refs.inputs[5].focus()
    },
    checkIFCodeIsFull() {
      var foundEmpty = false
      this.isCodeFull = false
      for (let i = 0; i < this.verificationCode.length; i++) {
        if (this.verificationCode[i] == '' || !/^[0-9]$/.test(this.verificationCode[i])) {
          foundEmpty = true
        }
      }
      if (!foundEmpty) {
        this.isCodeFull = true
      }
    },
    isCodeValid() {
      const code = this.verificationCode.toString().replaceAll(',', '')
      this.hasError = false
      this.$refs.inputs[5].blur()

      if (code === '') return (this.hasError = true)
      if (code.length < 6) return (this.hasError = true)
    },
    next() {
      const isRTL = this.Language === 'fa'
      const processedDigits = [...this.verificationCode]
      isRTL ? processedDigits.reverse() : processedDigits
      this.validateCaptcha(processedDigits.toString().replaceAll(',', ''))
      // this.$emit('proceed', this.verificationCode.toString().replaceAll(',', ''))
    },
    validateCaptcha(totp) {
      this.$recaptchaLoaded()
      this.recaptchaToken = this.$recaptcha('login')

      this.recaptchaToken
        .then(value => {
          this.login(value, totp)
        })
        .catch(err => {
          console.log(err)
        })
    },
    login(token, totp) {
      this.$auth
        .login({
          url: `${config.API.host}authenticate/login?isClient=1&totp=${totp}${window.localStorage.getItem('campaign_name') ? `&campaign_name=${window.localStorage.getItem('campaign_name')}` : ''}`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Basic ' + Base64.encode(this.data.email + ':' + this.data.password + ':' + token),
          },
          staySignedIn: this.data.rememberMe,
        })
        .then(
          () => {
            this.hasError = false
            this['menu/reset']()
            this.$auth.fetch().then(async res => {
              const emailVerificationResponse = await service.post('/email/get-verification-status', {
                email: this.data.email,
                userID: res.data.userID,
              })

              const isEmailVerified = emailVerificationResponse.data.verified
              this['user/setEmailVerified'](emailVerificationResponse.data)

              // If email is not verified, redirect to verify-email page
              if (this.hasError === false) {
                this.$nextTick(() => {
                  if (!isEmailVerified) {
                    this.$router.push('/' + (this.Language || 'en') + '/verify-email').catch(() => {})
                  } else this.$router.push(`/${this.Language}/`).catch(() => {})
                })
              }

              this.$bus.$emit('LiveFeedConnect')
            })
          },
          err => {
            if (err.response) {
              // if (err.response.data.meta && err.response.data.meta.messageID) {
              //   this.$toasted.error(this.$t(err.response.data.meta.messageID).tTitle())
              // } else {
              //   this.$toasted.error(err.response.data.detail)
              // }
              this.error = err.response.data
              this.$store.state.token = null
              this.hasError = true
            }
          }
        )
    },
    back() {
      this.$emit('back', this.is2FASetup)
    },
    ...mapActions(['logoLoader/set', 'menu/reset', 'user/setEmailVerified']),
  },
}
</script>
